import type { Competitor } from '../types'

// Sources (checked 2026-10-01): store.e-z.gg (USD), the e-z.gg FAQ, register,
// and bio dashboard pages (behind a Cloudflare challenge, read from the official
// site's own page bundles), and the public stats API at api.e-z.bio.
const ezbio: Competitor = {
  slug: 'ezbio',
  aliases: ['e-z-bio', 'ez-bio'],
  name: 'e-z.bio',
  tagline: 'Invite-only aesthetic biolink bundled with the e-z.host file host.',
  site: 'https://e-z.bio',
  pricingUrl: 'https://store.e-z.gg',
  checkedOn: '2026-10-01',
  seo: {
    title: 'e-z.bio alternative: mychar.gg vs e-z.bio compared (2026)',
    description: 'An e-z.bio alternative with a free plan, no invite code, a live editor, animated effects, video clips, and built-in analytics. Features and prices compared.'
  },
  hero: {
    title: 'The e-z.bio alternative you can start for free',
    description: 'No invite code needed. Build your page in a live drag-and-drop editor with animated effects, music, and clips, and see what visitors click.'
  },
  summary: 'mychar.gg is an e-z.bio alternative that you can start for free, without an invite code: it adds a live editor on the page itself, animated background effects, video clips that play in place, Twitch and YouTube sections, and analytics beyond a view counter. e-z.bio is cheaper to get into, at $3 once with a file host included, and it has strong Discord features.',
  chooseMychar: [
    'want to start free without buying or finding an invite',
    'want animated background effects and a live, on-page editor',
    'want analytics with clicks and top links, not only a view count',
    'want your own domain on your page'
  ],
  chooseThem: [
    'want the cheapest one-time price, with a file host and URL shortener',
    'want Discord avatar decorations and a server card at that price',
    'already use e-z.host for uploads'
  ],
  features: {
    free_plan: { yes: false, text: 'Invite code, $3 once' },
    paid_plans: 'Access $3, Premium upgrade $5, once',
    billing: 'Once',
    first_year: '$3 to $8 in total',
    custom_domain: { yes: false, text: 'Only for file host links' },
    editor: 'Settings form, then save',
    drag_drop: { yes: true, text: 'Reorder sections and socials' },
    themes: { yes: false, text: 'Color pickers, no templates' },
    layouts: '2',
    fonts: '14 presets',
    custom_fonts: false,
    effects: { yes: false, text: 'Blur, tilt, and intro animations' },
    cursors: { yes: true, text: 'Upload plus 5 effects' },
    video_background: true,
    custom_code: false,
    music_player: true,
    video_clips: { yes: false, text: 'Background video only' },
    comments: false,
    view_counter: true,
    badges: { yes: true, text: 'Premium and Discord badges' },
    youtube: 'Media embeds, up to 5',
    twitch: { yes: false, text: 'Link only' },
    discord: { yes: true, text: 'Presence and server card' },
    music_embeds: { yes: true, text: 'Through media embeds' },
    analytics_free: { yes: false, text: 'View counter only' },
    store: false,
    email_capture: false,
    mobile_app: false
  },
  plans: [
    { name: 'Invite code', price: '$3', period: 'one-time', note: 'Lifetime access to e-z.bio and the e-z.host file host. There is no free signup.' },
    { name: 'Premium upgrade', price: '$5', period: 'one-time', note: 'Bigger uploads, a second bio alias, emoji in your URL, a badge, and a Discord role.' }
  ],
  plansNote: 'USD. Invite codes can also come from existing members during invite waves.',
  advantages: [
    {
      title: 'Start free, no invite',
      description: 'Sign up and publish your page today on the Free plan. e-z.bio needs an invite code first.',
      icon: 'i-lucide-gift'
    },
    {
      title: 'Animated background effects',
      description: 'Rain, snow, fire, sakura, matrix, plasma, and more. e-z.bio offers blur, tilt, and an opening animation.',
      icon: 'i-lucide-sparkles'
    },
    {
      title: 'A live editor on the page',
      description: 'Edit text in place, drag things where you want them, and see every change live instead of saving a settings form.',
      icon: 'i-lucide-mouse-pointer-click'
    },
    {
      title: 'Analytics that go past a view count',
      description: 'Views, clicks, and top links on every plan, with live visitors, countries, and referrers on Pro.',
      icon: 'i-lucide-chart-line'
    },
    {
      title: 'Video clips and live sections',
      description: 'Clips that play in place, your Twitch live status and schedule, and your latest YouTube uploads.',
      icon: 'i-lucide-clapperboard'
    },
    {
      title: 'Your own domain and skins',
      description: 'Put your page on your own domain for €2.99 per month, and start from community skins instead of a blank page.',
      icon: 'i-lucide-globe'
    }
  ],
  strengths: [
    {
      title: 'The lowest price',
      description: 'Lifetime access is $3 once and Premium another $5, which also covers the e-z.host file host.'
    },
    {
      title: 'Strong Discord features',
      description: 'Live presence, a server invite card with member count, and Discord avatar decorations, without an upsell.'
    },
    {
      title: 'Media included',
      description: 'Video backgrounds, uploaded music, and cursor effects are part of the base access.'
    },
    {
      title: 'Extra tools',
      description: 'A file host with ShareX support, a URL shortener, and pastes come with the same account.'
    }
  ],
  sections: [
    {
      title: 'Why people look for an e-z.bio alternative',
      paragraphs: [
        'e-z.bio is a dark, aesthetic biolink with a loyal community and a very low price. The first hurdle is getting in: there is no free signup, so you buy an invite code or wait for one from a member. After that, the page is set up through a settings form that you save and then view, and analytics are limited to the view counter on your page.',
        'People who outgrow it usually want effects that move behind the page, clips that play in place, live Twitch and YouTube sections, a custom domain for the page itself, or a clearer picture of who clicks what.'
      ]
    },
    {
      title: 'How mychar.gg is different',
      paragraphs: [
        'mychar.gg keeps the profile-style look and adds a lot to build with. You design in an [editor](/docs/customization) that is your page in edit mode, with layouts, fonts, colors, video backgrounds, 13 animated effects on Elite, custom cursors, and trails. The music player is on every plan, clips play in place on Pro and Elite, and Discord, YouTube, and Twitch sections keep themselves current.',
        'The Free plan needs no invite and includes analytics for views, clicks, and top links. Pro is €6.99 once and Elite €12.99 once. Our [guns.lol alternatives post](/blog/guns-lol-alternatives-2026) also compares e-z.bio, and the [guide for gamers](/for/gamers) covers the game library and setup section.'
      ]
    }
  ],
  switchSteps: [
    { title: 'Claim your username', description: 'Sign up free at [mychar.gg/signup](/signup). No invite code needed.' },
    { title: 'Bring your assets', description: 'Upload your avatar, background, and music. Video backgrounds and cursor uploads start on Pro.' },
    { title: 'Rebuild the look', description: 'Pick a layout, font, and colors, and add an effect like rain or snow, or start from a community [skin](/skins).' },
    { title: 'Connect Discord', description: 'Show your Discord profile card, with your banner, name style, and badges. On Elite, your server invite can show as a live server card. See the [Discord guide](/docs/integrations/discord).' },
    { title: 'Swap the link', description: 'Replace your e-z.bio link in your bios and your Discord profile.' }
  ],
  faq: [
    {
      label: 'Do I need an invite code for mychar.gg?',
      content: 'No. Anyone can sign up and publish a page on the Free plan, with no card.'
    },
    {
      label: 'Is mychar.gg more expensive than e-z.bio?',
      content: 'Free costs nothing, and upgrades are one-time: Pro is €6.99 and Elite €12.99. e-z.bio access is $3 once and Premium $5 once, so it is cheaper if you only need its feature set.'
    },
    {
      label: 'Does mychar.gg show my Discord server?',
      content: 'Yes. Discord connects on every plan for your profile card, and on Elite a Discord invite link can show as a live server card with a join button.'
    },
    {
      label: 'Does mychar.gg have a file host like e-z.host?',
      content: 'No. mychar.gg is only the page. Your uploads, such as avatars, backgrounds, music, and clips, are for your page.'
    }
  ]
}

export default ezbio
